import React, { useState, useEffect } from "react";
import { Form, Card, Row, Col, Input, DatePicker, Select, Button, Space, Upload } from "antd";
import { FileImageOutlined, FileTextOutlined, PlusOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { ApiURL, baseURL } from "../../../../services/apiConstants";
import { post } from "../../../../services/services";

import dayjs from "dayjs";

const { TextArea } = Input;

const getValidDate = (date) => {
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
};

const getLastRecordDate = (...records) => {
  const dateFields = [
    "last_date_done",
    "counter_date",
    "last_counter_date",
    "last_update_date",
    "last_record_date",
    "last_done_date",
  ];

  for (const record of records) {
    for (const field of dateFields) {
      const parsedDate = getValidDate(record?.[field]);
      if (parsedDate) return parsedDate;
    }
  }

  return null;
};

const getAttachmentUrl = (file) => {
  const rawFileUrl =
    file?.file ||
    file?.attachment ||
    file?.file_url ||
    file?.url ||
    "";

  if (!rawFileUrl) return "";

  return rawFileUrl.startsWith("http")
    ? rawFileUrl
    : `${baseURL}${rawFileUrl}`;
};

const AttachmentGrid = ({ attachments }) => (
  <Row gutter={[16, 16]}>
    {attachments.map((file, index) => {
      const fileUrl = getAttachmentUrl(file);
      const fileName =
        file?.name ||
        file?.file_name ||
        `Attachment ${index + 1}`;
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl || "");

      return (
        <Col xs={24} sm={12} md={8} lg={6} key={file?.id || index}>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={false}
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              textDecoration: "none",
              color: "#000",
              background: "#fafafa",
              minHeight: "70px",
            }}
          >
            <span style={{ fontSize: "24px" }}>
              {isImage ? <FileImageOutlined /> : <FileTextOutlined />}
            </span>

            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </div>

              <div style={{ fontSize: "12px", color: "#888" }}>
                Click to view
              </div>
            </div>
          </a>
        </Col>
      );
    })}
  </Row>
);

const WorkOrderForm = ({
  form,
  selectedJobList,
  selectedWorkOrder,
  onSubmit,
  submitLoader,
  requiresOverdueReason,
  isCounterJob,
  viewOnly,
}) => {
  const [list_roll_permission, set_list_roll_permission] = useState([]);

  const tableCellStyle = {
    border: "1px solid #d9d9d9",
    padding: "12px",
    verticalAlign: "top",
  };
  const today = dayjs().startOf("day");
  const lastRecordDate = getLastRecordDate(selectedJobList, selectedWorkOrder);
  const disableInvalidCompletionDate = (current) => {
    const selectedDate = getValidDate(current);

    if (!selectedDate) return false;

    return (
      selectedDate.isAfter(today, "day") ||
      (lastRecordDate && selectedDate.isBefore(lastRecordDate, "day"))
    );
  };
  const validateCompletionDate = (_, value) => {
    const selectedDate = getValidDate(value);

    if (!selectedDate) return Promise.resolve();

    if (selectedDate.isAfter(today, "day")) {
      return Promise.reject(new Error("Future dates are not allowed"));
    }

    if (lastRecordDate && selectedDate.isBefore(lastRecordDate, "day")) {
      return Promise.reject(
        new Error(
          `Date cannot be before ${lastRecordDate.format("YYYY-MM-DD")}`
        )
      );
    }

    return Promise.resolve();
  };
  const [errors, set_errors] = useState({});
  const completedAttachments = selectedJobList?.work_order_attachments || [];
  const ListofRollPermissionStaff = () => {
    const FORM_DATA = new FormData();
    post(ApiURL.list_role_permission, FORM_DATA).then((res) => {
      if (res?.status == true) {
        set_list_roll_permission(res?.role_permission);
      } else {
        const errors = res?.errors || {};
        set_errors(errors);
      }
    });
  };

  useEffect(() => {
    ListofRollPermissionStaff();
  }, []);
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} disabled={viewOnly}>

      {/* General Info */}
      <Card title="General Information" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]}>
          <Col xs={24} md={6}>
            <Form.Item
              name="actual_completion_date"
              label="Actual Completion Date"
              rules={[
                { required: true, message: "Required" },
                { validator: validateCompletionDate },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={disableInvalidCompletionDate}
                inputReadOnly
              />
            </Form.Item>
          </Col>

          {isCounterJob(selectedJobList) && (<Col xs={24} md={6}>
            <Form.Item
              name="completion_counter"
              label="Completion Counter"
              rules={[
                {
                  required: isCounterJob(selectedJobList),
                  message: "Required",
                },
              ]}
            >
              <Input type="number" />
            </Form.Item>
          </Col>)}

          <Col xs={24} md={6}>
            <Form.Item name="downtime_hours" label="Downtime (Hours)">
              <Input type="number" />
            </Form.Item>
          </Col>

          <Col xs={24} md={6}>
            <Form.Item name="total_duration_hours" label="Duration (Hours)">
              <Input type="number" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Overdue */}
      {requiresOverdueReason && (
        <Card
          title="Overdue Reason"
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            name="overdue_reason"
            label="Reason"
            rules={[
              {
                required: true,
                message: "Required",
              },
            ]}
          >
            <TextArea rows={3} />
          </Form.Item>
        </Card>
      )}
      {/* Resources */}
      <Card
        title={<span style={{ fontWeight: 600 }}>Resources Used</span>}
        style={{
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Form.List
          name="resources"
          initialValue={[{ discipline: "", hours_spent: "" }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={[24, 16]} key={key} align="middle">

                  {/* Discipline */}
                  <Col xs={24} sm={12} md={11}>
                    <Form.Item
                      {...restField}
                      name={[name, "discipline"]}
                      label="Discipline"
                      rules={[
                        { required: true, message: "Please select discipline" },
                      ]}
                    >
                      <Select placeholder="--- Select Designation ---" style={{ width: "100%" }}>
                        {list_roll_permission
                          ?.filter((role) => role.status && !role.soft_delete)
                          .map((role) => (
                            <Select.Option key={role.id} value={role.title}>
                              {role.title}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Hours */}
                  <Col xs={24} sm={12} md={11}>
                    <Form.Item
                      {...restField}
                      name={[name, "hours_spent"]}
                      label="Hours"
                    >
                      <Input type="number" placeholder="Enter hours" />
                    </Form.Item>
                  </Col>

                  {/* Delete Icon */}
                  {fields.length > 1 && (
                    <Col xs={24} sm={24} md={2}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginTop: "6px" }}
                      />
                    </Col>
                  )}
                </Row>
              ))}

              {/* Add Button */}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({ discipline: "", hours_spent: "" })}
              >
                Add Resource
              </Button>
            </>
          )}
        </Form.List>

      </Card>

      {/* Stock Used */}
      <Card
        title={<span style={{ fontWeight: 600 }}>Stock Used</span>}
        style={{
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Form.List
          name="stock_used"
          initialValue={[
            {
              part_no: "",
              initial_rob: "",
              consumed: "",
              final_rob: "",
              remarks: "",
            },
          ]}
        >
          {(fields, { add, remove }) => (
            <>
              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "16px",
                  }}
                >
                  <thead>
                    <tr>
                      {[
                        "Part No",
                        "Initial ROB",
                        "Consumed",
                        "Final ROB",
                        "Remarks",
                        "Action",
                      ].map((header) => (
                        <th
                          key={header}
                          style={{
                            border: "1px solid #d9d9d9",
                            padding: "12px",
                            background: "#fafafa",
                            textAlign: "left",
                            fontWeight: 600,
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {fields.map(({ key, name, ...restField }) => {
                      // Values
                      const stockValues =
                        form.getFieldValue("stock_used") || [];

                      const currentRow = stockValues[name] || {};

                      const initialRob =
                        Number(currentRow.initial_rob || 0);

                      const consumed =
                        Number(currentRow.consumed || 0);

                      const finalRob =
                        initialRob - consumed;

                      return (
                        <tr key={key}>
                          {/* Part No */}
                          <td style={tableCellStyle}>
                            <Form.Item
                              {...restField}
                              name={[name, "part_no"]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="Part No" />
                            </Form.Item>
                          </td>

                          {/* Initial ROB */}
                          <td style={tableCellStyle}>
                            <Form.Item
                              {...restField}
                              name={[name, "initial_rob"]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                type="number"
                                placeholder="Initial ROB"
                              />
                            </Form.Item>
                          </td>

                          {/* Consumed */}
                          <td style={tableCellStyle}>
                            <Form.Item
                              {...restField}
                              name={[name, "consumed"]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                type="number"
                                placeholder="Consumed"
                              />
                            </Form.Item>
                          </td>

                          {/* Final ROB */}
                          <td style={tableCellStyle}>
                            <Form.Item shouldUpdate noStyle>
                              {() => {
                                const stockValues =
                                  form.getFieldValue("stock_used") || [];

                                const currentRow =
                                  stockValues[name] || {};

                                const initialRob = Number(
                                  currentRow?.initial_rob || 0
                                );

                                const consumed = Number(
                                  currentRow?.consumed || 0
                                );

                                const finalRob =
                                  initialRob - consumed;

                                return (
                                  <Input
                                    value={finalRob}
                                    disabled
                                    placeholder="Final ROB"
                                  />
                                );
                              }}
                            </Form.Item>
                          </td>

                          {/* Remarks */}
                          <td style={tableCellStyle}>
                            <Form.Item
                              {...restField}
                              name={[name, "remarks"]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="Remarks" />
                            </Form.Item>
                          </td>

                          {/* Delete */}
                          <td style={tableCellStyle}>
                            {fields.length > 1 && (
                              <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Add More */}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    part_no: "",
                    initial_rob: "",
                    consumed: "",
                    final_rob: "",
                    remarks: "",
                  })
                }
              >
                Add More
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Card
        title={
          <span style={{ fontWeight: 600 }}>
            History & Attachments
          </span>
        }
        style={{
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Row gutter={[24, 16]}>

          {/* Work History */}
          <Col xs={24}>
            <Form.Item
              name="work_history"
              label="Work History"
              rules={[
                {
                  required: true,
                  message: "Please enter work history",
                },
              ]}
            >
              <TextArea
                rows={5}
                placeholder="Enter detailed description of the work performed..."
                style={{
                  borderRadius: "6px",
                }}
              />
            </Form.Item>
          </Col>

          {/* Attachments */}
          <Col xs={24}>
            {completedAttachments.length > 0 && (
              <Form.Item label="Attachments">
                <AttachmentGrid attachments={completedAttachments} />
              </Form.Item>
            )}

            {!viewOnly && (
              <Form.Item
                name="attachments"
                label={completedAttachments.length > 0 ? "Upload Attachments" : "Attachments"}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }

                  return e?.fileList || [];
                }}
              >
                <Upload
                  multiple
                  beforeUpload={() => false}
                >
                  <Button
                    icon={<UploadOutlined />}
                  >
                    Click to Upload Attachments
                  </Button>
                </Upload>
              </Form.Item>
            )}
          </Col>
        </Row>
      </Card>


      {/* Submit */}
      {!viewOnly && (
        <div
          style={{
            textAlign: "center",
            marginTop: "24px",
            marginBottom: "20px",
          }}
        >
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={submitLoader}
            className="save_btn"
            style={{
              height: "48px",
              padding: "0 60px",
              borderRadius: "24px",
              fontWeight: 600,
              minWidth: "220px",
            }}
          >
            Complete Work Order
          </Button>
        </div>)}
    </Form>
  );
};

export default WorkOrderForm;
